import { useState, useEffect } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { authClient, useAuthSession } from "../lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === "string" ? { redirect: search.redirect } : {},
  component: LoginPage,
});

type Step = "choose" | "email" | "code";
type Mode = "signin" | "signup";

function LoginPage() {
  const router = useRouter();
  const { redirect } = Route.useSearch();
  const { data: session } = useAuthSession();
  const [step, setStep] = useState<Step>("choose");
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (session?.user) {
      void router.navigate({ to: redirect ?? "/dashboard" });
    }
  }, [session, router, redirect]);

  async function handleSendOtp() {
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "sign-in",
      });
      if (result.error) {
        setError(result.error.message ?? "Failed to send code.");
      } else {
        setStep("code");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp() {
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.signIn.emailOtp({ email, otp: code });
      if (result.error) {
        setError(result.error.message ?? "Invalid code.");
      } else {
        await router.navigate({ to: redirect ?? "/dashboard" });
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 relative overflow-hidden">
      <div aria-hidden className="breathing-orb" style={{ top: "10%" }} />
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border-2 border-primary mx-auto">
            <div className="w-2 h-2 rounded-full bg-primary" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Consistency Circle</h1>
          <p className="text-sm text-muted-foreground">
            Small steps, kept gently — with people who care.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {step === "choose" && "Welcome"}
              {step === "email" && (mode === "signin" ? "Sign in" : "Create account")}
              {step === "code" && "Check your email"}
            </CardTitle>
            <CardDescription>
              {step === "choose" && "Sign in or create a new account"}
              {step === "email" && "Enter your email address to continue"}
              {step === "code" && `We sent a 6-digit code to ${email}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === "choose" && (
              <>
                <Button
                  className="w-full"
                  onClick={() => { setMode("signin"); setStep("email"); }}
                >
                  Sign in
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => { setMode("signup"); setStep("email"); }}
                >
                  Create account
                </Button>
              </>
            )}

            {step === "email" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && email) void handleSendOtp(); }}
                    placeholder="you@example.com"
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button
                  className="w-full"
                  onClick={() => void handleSendOtp()}
                  disabled={!email || loading}
                >
                  {loading ? "Sending…" : "Send code"}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => { setStep("choose"); setError(null); }}
                >
                  Back
                </Button>
              </>
            )}

            {step === "code" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="code">Verification code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    className="font-mono tracking-widest text-center text-lg"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6) void handleVerifyOtp(); }}
                    placeholder="000000"
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button
                  className="w-full"
                  onClick={() => void handleVerifyOtp()}
                  disabled={code.length !== 6 || loading}
                >
                  {loading ? "Verifying…" : "Verify"}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => { setStep("email"); setCode(""); setError(null); }}
                >
                  Resend code
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
